import { ApexOptions } from 'apexcharts';
import dynamic from 'next/dynamic';
import type { Props } from 'react-apexcharts';

const ApexChart = dynamic<Props>(async () => import('react-apexcharts'), {
  ssr: false,
  loading: () => null,
});

export const Chart = ApexChart;

export const getCommonTimelineChartOptionsStatic = (): ApexOptions => ({
  chart: {
    animations: { enabled: false },
    background: 'transparent',
    toolbar: { show: false },
  },
  dataLabels: { enabled: false },
  grid: {
    borderColor: 'var(--border)',
    strokeDashArray: 2,
  },
  legend: {
    horizontalAlign: 'right',
    labels: { colors: 'var(--muted-foreground)' },
    position: 'top',
    show: true,
  },
  stroke: {
    curve: 'smooth',
    lineCap: 'square',
    width: 3,
  },
  theme: { mode: 'dark' },
  xaxis: {
    type: 'datetime',
    labels: {
      style: {
        colors: 'var(--muted-foreground)',
      },
      formatter: function (value) {
        return new Date(value).toLocaleDateString('en-US', {
          timeZone: 'UTC',
          month: 'short',
          day: 'numeric',
        });
      },
    },
    tooltip: {
      formatter: function (value) {
        return new Date(value).toLocaleDateString('en-US', {
          timeZone: 'UTC',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
  },
  yaxis: {
    labels: {
      style: {
        colors: 'var(--muted-foreground)',
      },
      formatter: (value) => value?.toFixed(0),
    },
  },
  markers: {
    hover: { size: undefined, sizeOffset: 2 },
    shape: 'circle',
    size: 3,
    strokeWidth: 0,
  },
  tooltip: {
    intersect: false,
    shared: true,
    x: {
      formatter: function (value) {
        return new Date(value).toLocaleDateString('en-US', {
          timeZone: 'UTC',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
  },
});

export const getCommonBarChartOptionsStatic = (): ApexOptions => ({
  chart: {
    animations: { enabled: false },
    background: 'transparent',
    toolbar: { show: false },
  },
  dataLabels: { enabled: false },
  grid: {
    borderColor: 'var(--border)',
    strokeDashArray: 2,
  },
  legend: {
    horizontalAlign: 'right',
    labels: { colors: 'var(--muted-foreground)' },
    position: 'top',
    show: true,
  },
  plotOptions: {
    bar: {
      borderRadius: 4,
      columnWidth: '60%',
    },
  },
  stroke: {
    show: true,
    width: 2,
    colors: ['transparent'],
  },
  theme: { mode: 'dark' },
  xaxis: {
    type: 'datetime',
    labels: {
      style: {
        colors: 'var(--muted-foreground)',
      },
      formatter: function (value) {
        return new Date(value).toLocaleDateString('en-US', {
          timeZone: 'UTC',
          month: 'short',
          day: 'numeric',
        });
      },
    },
    tooltip: {
      formatter: function (value) {
        return new Date(value).toLocaleDateString('en-US', {
          timeZone: 'UTC',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
  },
  yaxis: {
    labels: {
      style: {
        colors: 'var(--muted-foreground)',
      },
      formatter: (value) => value?.toFixed(0),
    },
  },
  tooltip: {
    x: {
      formatter: function (value) {
        return new Date(value).toLocaleDateString('en-US', {
          timeZone: 'UTC',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
  },
});

// Legacy theme-based functions kept for backward compat but use static CSS vars instead
export const getCommonTimelineChartOptions = (_theme?: any): ApexOptions => getCommonTimelineChartOptionsStatic();

export const getCommonBarChartOptions = (_theme?: any): ApexOptions => getCommonBarChartOptionsStatic();
